import { useCallback, useEffect, useState } from "react";
import { Button, Card, Checkbox, Col, Divider, Form, Input, Modal, Row, Space, Typography } from "antd";
import { SaveOutlined, UndoOutlined } from '@ant-design/icons';
import { apiGetSystemConfig, apiUpdateSystemConfig } from "../../api";
import { SystemConfig } from "../../interfaces/system-config.interface";
import { toastError, toastSuccess } from "../../utils/toast";
import { useLoading } from "../../context/LoadingContext";

const { Title } = Typography;

/**
 * Display name mapping for configuration keys
 */
const CONFIG_DISPLAY_NAMES: Record<string, string> = {
  'NotifyTypeTransactionFail': 'Thông báo giao dịch thất bại',
  'NotifyTypeTransactionSuccess': 'Thông báo giao dịch thành công',
  'NotifyTypeTransactionUnknownResult': 'Thông báo giao dịch kết quả không xác định',
  'NotifyTypeTransactionCheckAndRetry': 'Thông báo kiểm tra và thử lại giao dịch',
  'NotifyTypeSendNotify': 'Gửi thông báo',
  'NotifyTypeSendNotifySlack': 'Gửi thông báo Slack',
};

/**
 * Helper to get display name for a config key
 */
const getDisplayName = (key: string): string => {
  return CONFIG_DISPLAY_NAMES[key] || key;
};

/**
 * Helper to detect if a value is a boolean string
 */
const isBooleanValue = (value: string): boolean => {
  return value === 'true' || value === 'false';
};

/**
 * Helper to parse boolean values for display
 */
const parseBooleanValue = (value: string): boolean => {
  return value === 'true';
};

/**
 * System Configuration Management Page
 */
export default function SystemConfigurationPage() {
  const [form] = Form.useForm();
  const { setIsLoading } = useLoading();

  const [systemConfigs, setSystemConfigs] = useState<SystemConfig[]>([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [otpModalVisible, setOtpModalVisible] = useState(false);
  const [otpValue, setOtpValue] = useState('');

  /**
   * Fetch system configuration list from API
   */
  const fetchSystemConfig = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await apiGetSystemConfig();

      let configsList: SystemConfig[] = [];

      if (response.data && response.data.systemConfigsList && response.data.systemConfigsList.length > 0) {
        for (const config of response.data.systemConfigsList) {
          config.value = !parseBooleanValue(config.value);
        }
        configsList = response.data.systemConfigsList;
      }

      setSystemConfigs(configsList);

      // Initialize form values
      const initialValues: any = {};
      configsList.forEach((config: SystemConfig) => {
        if (isBooleanValue(config.value)) {
          initialValues[config.key] = parseBooleanValue(config.value);
        } else {
          initialValues[config.key] = config.value;
        }
      });
      form.setFieldsValue(initialValues);
    } catch (error) {
      console.error('Error fetching system config, using mock data:', error);
      setSystemConfigs([]);
      form.setFieldsValue({});
      toastError('Không thể tải cấu hình từ server, sử dụng dữ liệu mẫu');
    } finally {
      setIsLoading(false);
    }
  }, [setIsLoading, form]);

  /**
   * Handle form value changes
   */
  const handleFormChange = () => {
    const formValues = form.getFieldsValue();
    const hasAnyChange = systemConfigs.some((config) => {
      const originalValue = isBooleanValue(config.value) ? parseBooleanValue(config.value) : config.value;
      const currentValue = formValues[config.key];
      return currentValue !== originalValue;
    });
    setHasChanges(hasAnyChange);
  };

  /**
   * Cancel changes and reset form
   */
  const handleCancelEdit = () => {
    if (hasChanges) {
      Modal.confirm({
        title: 'Xác nhận hủy',
        content: 'Bạn có chắc chắn muốn hủy? Tất cả thay đổi sẽ bị mất.',
        okText: 'Đồng ý',
        cancelText: 'Không',
        onOk: () => {
          const initialValues: any = {};
          systemConfigs.forEach((config: SystemConfig) => {
            if (isBooleanValue(config.value)) {
              initialValues[config.key] = parseBooleanValue(config.value);
            } else {
              initialValues[config.key] = config.value;
            }
          });
          form.setFieldsValue(initialValues);
          setHasChanges(false);
        },
      });
    }
  };

  /**
   * Show OTP modal for confirmation
   */
  const handleSaveClick = async () => {
    try {
      await form.validateFields();
      setOtpModalVisible(true);
    } catch (error) {
      toastError('Vui lòng kiểm tra lại các trường thông tin');
    }
  };

  /**
   * Submit updated configuration to API
   */
  const handleSubmitUpdate = async () => {
    if (!otpValue || otpValue.trim() === '') {
      toastError('Vui lòng nhập mã OTP xác nhận');
      return;
    }

    try {
      setIsLoading(true);
      const formValues = form.getFieldsValue();

      // Build updated config list
      const updatedConfigs = systemConfigs.reduce((acc: SystemConfig[], config) => {
        const newValue = formValues[config.key];
        if (newValue !== config.value) {
          acc.push({
            key: config.key,
            value: String(!newValue),
          });
        }

        return acc;
      }, []);

      const response = await apiUpdateSystemConfig({
        systemConfigsList: updatedConfigs,
        otpConfirm: otpValue,
      });

      if (response.status === 200) {
        toastSuccess(response.data.message || 'Cập nhật cấu hình thành công');
        setOtpModalVisible(false);
        setOtpValue('');
        setHasChanges(false);
        fetchSystemConfig();
      }
    } catch (error: any) {
      console.error('Error updating system config:', error);
      const errorMessage = error?.response?.data?.message || 'Cập nhật cấu hình thất bại';
      toastError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Render form field based on value
   */
  const renderFormField = (config: SystemConfig) => {
    const { key, value } = config;

    return (
      <Form.Item name={key} valuePropName="checked" style={{ marginBottom: 0 }}>
        <Checkbox>{getDisplayName(config.key)}</Checkbox>
      </Form.Item>
    );
  };

  // Load data on component mount
  useEffect(() => {
    fetchSystemConfig();
  }, [fetchSystemConfig]);

  // Warn user before leaving if there are unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasChanges]);

  return (
    <div>
      {/* Header with Save/Cancel buttons */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4} style={{ margin: 0 }}>
          Quản lý cấu hình hệ thống
        </Title>
        <Space>
          <Button icon={<UndoOutlined />} onClick={handleCancelEdit} disabled={!hasChanges}>
            Hủy
          </Button>
          <Button
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSaveClick}
            disabled={!hasChanges}
          >
            Lưu thay đổi
          </Button>
        </Space>
      </div>

      {systemConfigs.length === 0 ? (
        <Card>
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <Typography.Text type="secondary">Không có cấu hình hệ thống nào</Typography.Text>
          </div>
        </Card>
      ) : (
        <Form form={form} layout="vertical" onValuesChange={handleFormChange}>
          {/* Notifications Configuration Section */}
          <Row gutter={[16, 16]}>
            {systemConfigs.map((config) => (
              <Col xs={24} sm={12} lg={8} key={config.key}>
                  {renderFormField(config)}
              </Col>
            ))}
          </Row>
        </Form>
      )}

      {/* OTP Confirmation Modal */}
      <Modal
        title="Xác nhận cập nhật cấu hình"
        open={otpModalVisible}
        onOk={handleSubmitUpdate}
        onCancel={() => {
          setOtpModalVisible(false);
          setOtpValue('');
        }}
        okText="Xác nhận"
        cancelText="Hủy"
      >
        <Divider />
        <p>
          <span style={{ color: 'red' }}>*</span> Nhập mã OTP xác nhận để cập nhật cấu hình hệ thống
        </p>
        <Input
          placeholder="Nhập mã OTP"
          value={otpValue}
          onChange={(e) => setOtpValue(e.target.value)}
          onPressEnter={handleSubmitUpdate}
        />
      </Modal>
    </div>
  );
}
